import { mineAPI } from './api/MineAPI.js';
import { filterAPI } from './api/FilterAPI.js';
import { statisticsAPI } from './api/StatisticsAPI.js';
import { dataCache } from './cache/DataCache.js';
import { normalizeData, categorizeCommodity, processMines } from '../utils/dataHelpers.js';

/**
 * DataService - Coordinates API calls with caching layer
 *
 * Simplified service that focuses on data coordination between
 * API modules and cache layer. No longer duplicates API functionality.
 */
class DataService {
  constructor() {
    // Use new cache layer instead of manual cache object
    this.cache = dataCache;
  }

  /**
   * Fetch mines with parameters (delegates to MineAPI)
   */
  async fetchMines(params = {}) {
    try {
      // Ensure params has proper structure for MineAPI
      const normalizedParams = {
        bounds: params.bounds || {
          west: -180,
          south: -90,
          east: 180,
          north: 90
        },
        zoom: params.zoom || 2,
        filters: params.filters || {}
      };

      const response = await mineAPI.fetchMinesInViewport(normalizedParams);
      if (response.success) {
        return normalizeData(response.data);
      } else {
        throw new Error(response.message || 'API request failed');
      }
    } catch (error) {
      console.error('Failed to fetch mines:', error);
      throw new Error('Unable to load mine data');
    }
  }

  /**
   * Fetch mines by status (uses fetchMines internally)
   */
  async fetchMinesByStatus(statuses) {
    try {
      const params = {
        bounds: {
          west: -180,
          south: -90,
          east: 180,
          north: 90
        },
        zoom: 2,
        filters: {
          status: statuses
        }
      };
      const response = await mineAPI.fetchMinesInViewport(params);

      if (response.success && response.data && response.data.features) {
        return response.data.features.map(feature => feature.properties);
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch mines by status:', error);
      throw new Error(`Unable to load mines with status: ${statuses.join(', ')}`);
    }
  }

  /**
   * Search mines (delegates to MineAPI)
   */
  async searchMines(query) {
    try {
      const response = await mineAPI.searchMines(query);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Search failed');
      }
    } catch (error) {
      console.error('Failed to search mines:', error);
      throw new Error('Search failed');
    }
  }

  /**
   * Get mine details (delegates to MineAPI)
   */
  async getMineDetails(mineId) {
    try {
      const response = await mineAPI.getMineDetails(mineId);
      if (response.success) {
        // API returns { mine: {...}, meta: {...} } - extract just the mine object
        const mine = response.data.mine || response.data;

        // Flatten mine_risk_aggregate to avoid nested object issues
        if (mine.mine_risk_aggregate && typeof mine.mine_risk_aggregate === 'object') {
          mine.risk_overall_score = mine.mine_risk_aggregate.overall_score;
          mine.risk_overall_score_50 = mine.mine_risk_aggregate.overall_score_50;
          mine.risk_classification = mine.mine_risk_aggregate.risk_classification;
          mine.risk_completeness_percentage = mine.mine_risk_aggregate.completeness_percentage;
        }

        return mine;
      } else {
        throw new Error(response.message || 'Failed to get mine details');
      }
    } catch (error) {
      console.error('Failed to get mine details:', error);
      throw new Error('Unable to load mine details');
    }
  }

  /**
   * Get countries with caching
   * Uses new cache layer and FilterAPI
   */
  async getCountries() {
    // Check cache first
    const cached = this.cache.getReferenceData('countries');
    if (cached) {
      return cached;
    }

    try {
      const response = await filterAPI.fetchCountries();
      if (response.success) {
        const countries = response.data.countries || [];
        // Cache the data
        this.cache.setReferenceData('countries', countries);
        return countries;
      } else {
        throw new Error(response.message || 'Failed to fetch countries');
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      throw new Error('Unable to load countries');
    }
  }

  /**
   * Get commodities with caching
   * Uses new cache layer and FilterAPI
   */
  async getCommodities() {
    // Check cache first
    const cached = this.cache.getReferenceData('commodities');
    if (cached) {
      return cached;
    }

    try {
      const response = await filterAPI.fetchCommodities();
      if (response.success) {
        const commodities = response.data.commodities || [];
        // Cache the data
        this.cache.setReferenceData('commodities', commodities);
        return commodities;
      } else {
        throw new Error(response.message || 'Failed to fetch commodities');
      }
    } catch (error) {
      console.error('Failed to fetch commodities:', error);
      throw new Error('Unable to load commodities');
    }
  }

  /**
   * Get filter options with caching
   * Uses new cache layer and FilterAPI
   */
  async getFilterOptions() {
    // Check cache first
    const cached = this.cache.getAllFilterOptions();
    if (cached) {
      return cached;
    }

    try {
      const response = await filterAPI.fetchFilterOptions();
      if (response.success) {
        // Cache the data
        this.cache.setAllFilterOptions(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch filter options');
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      throw new Error('Unable to load filter options');
    }
  }

  /**
   * Get statistics (delegates to StatisticsAPI)
   */
  async getStatistics() {
    try {
      const response = await statisticsAPI.getStatistics();
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch statistics');
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      throw new Error('Unable to load statistics');
    }
  }

  /**
   * Apply filters (delegates to MineAPI)
   */
  async applyFilters(filters, bounds = null, zoom = null) {
    try {
      const params = {
        bounds: bounds,
        zoom: zoom,
        filters: filters
      };

      const response = await mineAPI.fetchMinesInViewport(params);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to apply filters');
      }
    } catch (error) {
      console.error('Failed to apply filters:', error);
      throw new Error('Unable to apply filters');
    }
  }

  /**
   * Load initial data for application startup
   * Coordinates multiple API calls and data processing
   */
  async loadInitialMines() {
    try {
      const [countries, commodities, mines] = await Promise.all([
        this.getCountries(),
        this.getCommodities(),
        this.fetchMinesByStatus(['Development', 'Active'])
      ]);

      const countryMap = this.createCountryMap(countries);
      const commodityMap = this.createCommodityMap(commodities);

      const processedMines = processMines(mines, commodityMap, countryMap);

      return {
        mines: processedMines,
        countries,
        commodities,
        countryMap,
        commodityMap
      };
    } catch (error) {
      console.error('Failed to load initial data:', error);
      throw error;
    }
  }

  /**
   * Create country mapping (utility method)
   */
  createCountryMap(countries) {
    return countries.reduce((acc, country) => ({
      ...acc,
      [country.country_id]: country.country_name
    }), {});
  }

  /**
   * Create commodity mapping (utility method)
   */
  createCommodityMap(commodities) {
    return commodities.reduce((acc, commodity) => ({
      ...acc,
      [commodity.commodity_id]: commodity.commodity_name
    }), {});
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.cache.clearAll();
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    return this.cache.getStats();
  }
}

export const dataService = new DataService();